'use client';

import {
  retryTranslate,
  type TranslationSession,
} from '@/app/services/translation';
import DownloadButton from '@/components/histories/download-button';
import { useTranslateHistoryPolling } from '@/components/histories/translate-history-polling';
import {
  DocxIcon,
  EpubIcon,
  HtmlIcon,
  PDFIcon,
  PptIcon,
  TxtIcon,
  XlsxIcon,
} from '@/components/icons';
import {
  fetchSidebarTranslationHistory,
  isSidebarTranslationProcessing,
  sidebarHistoryKeys,
} from '@/components/sidebar-history/sidebar-history-cache';
import { SmartLink } from '@/components/smart-link';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { getFileExtension } from '@/lib/format-utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircleIcon,
  LanguagesIcon,
  MoreVerticalIcon,
  RotateCwIcon,
} from 'lucide-react';
import { toast } from 'sonner';

interface TranslateSidebarHistoryListProps {
  currentTranslationId: string | null;
  isTranslateRoute: boolean;
  isAuthenticated: boolean;
}

export function TranslateSidebarHistoryList({
  currentTranslationId,
  isTranslateRoute,
  isAuthenticated,
}: TranslateSidebarHistoryListProps) {
  const queryClient = useQueryClient();
  const { data: translations, isLoading } = useQuery({
    queryKey: sidebarHistoryKeys.translate,
    queryFn: fetchSidebarTranslationHistory,
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
  });

  useTranslateHistoryPolling({
    kind: 'sidebar',
    queryClient,
    items: translations ?? [],
    pageSize: 25,
  });

  const renderTranslationFileIcon = (fileName: string) => {
    const extension = getFileExtension(fileName);
    switch (extension) {
      case 'pdf':
        return (
          <PDFIcon className="h-4 w-4 shrink-0 text-red-500 dark:text-red-400 fill-background" />
        );
      case 'html':
        return (
          <HtmlIcon className="h-4 w-4 shrink-0 text-neutral-500 dark:text-neutral-400 fill-background" />
        );
      case 'docx':
        return (
          <DocxIcon className="h-4 w-4 shrink-0 text-blue-500 dark:text-blue-400 fill-background" />
        );
      case 'txt':
        return (
          <TxtIcon className="h-4 w-4 shrink-0 text-cyan-500 dark:text-cyan-400 fill-background" />
        );
      case 'xlsx':
        return (
          <XlsxIcon className="h-4 w-4 shrink-0 text-green-500 dark:text-green-400 fill-background" />
        );
      case 'pptx':
        return (
          <PptIcon className="h-4 w-4 shrink-0 text-orange-500 dark:text-orange-400 fill-background" />
        );
      case 'epub':
        return (
          <EpubIcon className="h-4 w-4 shrink-0 text-teal-500 dark:text-teal-400 fill-background" />
        );
      default:
        return <LanguagesIcon className="h-3.5 w-3.5 shrink-0" />;
    }
  };

  const renderTranslationProgressRing = (item: TranslationSession) => {
    const normalizedProgress = Math.max(
      0,
      Math.min(100, Math.round(item.global_progress_percent || 0)),
    );
    const radius = 8;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset =
      circumference - (normalizedProgress / 100) * circumference;

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="absolute right-1 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center text-primary ">
            <svg
              className="h-4 w-4 -rotate-90 bg-sidebar rounded-full"
              viewBox="0 0 24 24"
            >
              <circle
                cx="12"
                cy="12"
                r={radius}
                fill="none"
                stroke="currentColor"
                strokeOpacity="0.18"
                strokeWidth="3"
              />
              <circle
                cx="12"
                cy="12"
                r={radius}
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
              />
            </svg>
          </span>
        </TooltipTrigger>
        <TooltipContent>{normalizedProgress}%</TooltipContent>
      </Tooltip>
    );
  };

  if (isLoading) {
    return Array.from({ length: 8 }).map((_, index) => (
      <div key={index} className="px-2 py-1">
        <Skeleton className="h-4 w-full rounded-md" />
      </div>
    ));
  }

  if (!translations || translations.length === 0) {
    return (
      <div className="px-2 py-1.5 text-sm text-muted-foreground">
        暂无翻译记录
      </div>
    );
  }

  return translations.map((item: TranslationSession) => {
    const active =
      isTranslateRoute &&
      currentTranslationId === item.file_translation_session_id;
    const isProcessing = isSidebarTranslationProcessing(item.status);
    const isDone = item.status === 'DONE';
    const isError = item.status === 'ERROR';

    return (
      <div
        key={item.file_translation_session_id}
        className={`group/item relative rounded-lg ${
          active ? 'bg-sidebar-accent' : 'hover:bg-sidebar-accent'
        }`}
      >
        <SmartLink
          href={`/translate/preview?id=${item.file_translation_session_id}`}
          authenticated={isAuthenticated}
          mode="intent"
          className="flex items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:text-sidebar-accent-foreground"
        >
          {renderTranslationFileIcon(item.source_file_name)}
          <span className="line-clamp-1 break-all">
            {item.source_file_name}
          </span>
        </SmartLink>
        {isError && (
          <span className="bg-sidebar pointer-events-none absolute right-1 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center text-destructive transition-opacity group-hover/item:opacity-0 data-[state=open]:opacity-0">
            <AlertCircleIcon className="h-3.5 w-3.5" />
          </span>
        )}
        {isProcessing ? (
          renderTranslationProgressRing(item)
        ) : isDone ? (
          <div className="absolute right-1 top-1/2 -translate-y-1/2">
            <DownloadButton
              lang={item.target_lang}
              sid={item.file_translation_session_id}
              filename={item.source_file_name}
              title={item.title}
              compact
              icon="more"
              showItemIcons
              triggerClassName={`h-7 w-7 rounded-full opacity-0 transition-opacity group-hover/item:opacity-100 data-[state=open]:opacity-100 ${
                active
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground hover:bg-sidebar-accent/80'
                  : 'text-muted-foreground group-hover/item:bg-sidebar-accent group-hover/item:text-sidebar-accent-foreground hover:bg-sidebar-accent/80 hover:text-sidebar-accent-foreground data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
              }`}
              contentClassName="w-36 rounded-2xl p-2 shadow-sm"
              itemClassName="rounded-xl"
            />
          </div>
        ) : isError ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className={`absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 rounded-full opacity-0 transition-opacity group-hover/item:opacity-100 data-[state=open]:opacity-100 ${
                  active
                    ? 'bg-sidebar-accent text-sidebar-accent-foreground hover:bg-sidebar-accent/80'
                    : 'text-muted-foreground group-hover/item:bg-sidebar-accent group-hover/item:text-sidebar-accent-foreground hover:bg-sidebar-accent/80 hover:text-sidebar-accent-foreground data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
                }`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
              >
                <MoreVerticalIcon className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-36 rounded-2xl p-2 shadow-sm"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <DropdownMenuItem
                className="rounded-xl"
                onClick={async () => {
                  try {
                    await retryTranslate(item.file_translation_session_id);
                    toast.success('已重新提交翻译');
                  } catch (error) {
                    console.error(error);
                    toast.error('重试失败，请稍后再试');
                  }
                }}
              >
                <RotateCwIcon className="h-4 w-4" />
                重新翻译
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    );
  });
}
